import React, { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconFluorescentLamp } from '../../../../../../assets/control-icons/icon-fluorescent-lamp';
import { IconDeviceFlushMountedController } from '../../../../../../assets/device-icons';
import { Header, InputSelect, NavHeader, Page } from '../../../../../../components';
import FixedTwoButtons from '../../../../../../components/fixed-two-buttons';
import { ROUTES } from '../../../../../../routes/routes';
import ConfigurationInput from '../../components/input-controller';
import { StatusItem } from '../../components/status-item';
import { useBluetoothContext } from '../../context';
import { GateTypePrefix, IdentifyFormValues } from '../../types';
import { deviceSubTypesIcon, smartAWGateWithNoPhotoCell } from '../../utils';
import './index.scss';

const DeviceAddBluetoothIdentify: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('device-add');
  const { t: tc } = useTranslation('common');
  const {
    deviceNameBT,
    bluetoothData,
    identifing,
    typeOptions,
    isAlreadyAdded,
    advancedSettings,
    identifyDevice,
    checkIfConnected,
    updateBluetoothData,
  } = useBluetoothContext();
  const form = useForm<IdentifyFormValues>({
    mode: 'all',
    defaultValues: {
      name: '',
      type: '',
      subTypes: [],
    },
  });
  const { type, subTypes } = form.watch();

  useEffect(() => {
    checkIfConnected();

    if (bluetoothData.deviceName) form.setValue('name', bluetoothData.deviceName);
    if (bluetoothData.type) form.setValue('type', bluetoothData.type);

    if (bluetoothData.subTypes.length) form.setValue('subTypes', bluetoothData.subTypes);
    else {
      if (advancedSettings?.currentSubTypes?.length) {
        form.setValue('subTypes', advancedSettings.currentSubTypes);
      }
    }
  }, []);

  const subTypeOptions = useMemo(() => {
    if (advancedSettings?.subTypesInfo[type]) {
      const { availableSubTypes } = advancedSettings.subTypesInfo[type];

      return availableSubTypes.map((subType) => ({
        label: t(`deviceTypes.gateSubTypes.${subType}`),
        value: subType,
        icon: deviceSubTypesIcon[subType],
      }));
    }

    return [];
  }, [type, advancedSettings]);

  const handleTypeChange = (newType: string) => {
    const { type } = form.getValues();

    if (type === newType) return;

    form.setValue('type', newType);

    if (advancedSettings?.subTypesInfo[newType]) {
      form.setValue('subTypes', Array(advancedSettings.subTypesInfo[newType].maxSubTypes).fill(''));
    } else {
      form.setValue('subTypes', []);
    }
  };

  const onSubmit = (formData: IdentifyFormValues) => {
    updateBluetoothData({
      name: formData.name,
      type: formData.type,
      subTypes: formData.subTypes,
    });

    if (deviceNameBT.includes(GateTypePrefix.SmartAwGate)) {
      if (smartAWGateWithNoPhotoCell.includes(formData.type.toUpperCase())) {
        navigate(ROUTES.DeviceAddBluetoothNetwork());
      } else {
        navigate(ROUTES.DeviceAddBluetoothWisniowskiTutorialStep0());
      }
    } else {
      navigate(ROUTES.DeviceAddBluetoothNetwork());
    }
  };

  return (
    <Page
      isStickyHeader
      className="drive-add-identify"
      header={
        <>
          <NavHeader />
          <Header title={t('bluetooth.deviceIdentifing')} isUnderline column>
            {identifing && (
              <div className="drive-add-identify--header">
                <IconFluorescentLamp isOn />
                <span>{t('bluetooth.identifyActive')}</span>
              </div>
            )}
          </Header>
        </>
      }
    >
      <>
        <div className="device-bluetooth">
          <IconDeviceFlushMountedController />
          <div className="device-bluetooth--info">
            <p className="device-name">{bluetoothData.deviceName || 'N/A'}</p>
            <p className="mac-address">{`MAC: ${bluetoothData.mac}`}</p>
          </div>
        </div>
        {isAlreadyAdded && <StatusItem label={t('bluetooth.isAlreadyAddedToCloud')} value={isAlreadyAdded} />}
        {bluetoothData.diagnostic && isAlreadyAdded && (
          <div>
            <StatusItem
              label={t('bluetooth.deviceCloudConnected')}
              value={bluetoothData.diagnostic.lavvaCloudConnected}
            />
            <StatusItem
              label={t('bluetooth.deviceInternetConnected')}
              value={bluetoothData.diagnostic.internetConnected}
            />
            <StatusItem label={t('bluetooth.deviceWifiConnected')} value={bluetoothData.diagnostic.wifiIsConnected} />
          </div>
        )}
        <FormProvider {...form}>
          <form className="grid-list-24 m-t-32">
            <ConfigurationInput name="name" label={t('bluetooth.name')} placeholder={t('bluetooth.namePlaceholder')} />
            <Controller
              name="type"
              control={form.control}
              rules={{
                required: tc('isRequired') as string,
              }}
              render={({ field, fieldState: { error } }) => (
                <InputSelect
                  onChange={handleTypeChange}
                  value={field.value}
                  options={typeOptions}
                  label={t('bluetooth.deviceKind')}
                  name={field.name}
                  disabled={typeOptions.length <= 1 || isAlreadyAdded}
                  {...(error ? { errorMessage: error.message } : {})}
                />
              )}
            />
            {type &&
              subTypes.map((_, index) => (
                <Controller
                  key={index}
                  name={`subTypes.${index}`}
                  control={form.control}
                  rules={{
                    required: tc('isRequired') as string,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <InputSelect
                      onChange={field.onChange}
                      value={field.value}
                      options={subTypeOptions}
                      label={`${t('bluetooth.deviceType')} ${index + 1}`}
                      name={field.name}
                      {...(error ? { errorMessage: error.message } : {})}
                    />
                  )}
                />
              ))}
          </form>
          <FixedTwoButtons
            titlePrimary={tc('buttons.next')}
            titleSecondary={t('bluetooth.identify')}
            handleClickPrimary={form.handleSubmit(onSubmit)}
            {...(bluetoothData.identifyIsAvailable ? { handleClickSecondary: identifyDevice } : {})}
            reverse
          />
        </FormProvider>
      </>
    </Page>
  );
};

export default DeviceAddBluetoothIdentify;
